/**
 * 统一响应信封解析。
 *
 * 约束来源：CONTRACT.md 第 17 行
 * Envelope {code,msg,err,requestId?,data?}；**code 0 只表示提交成功**，
 * 绝不代表"已送达/已读"。因此这里只有 code === 0 才返回 data，
 * 任何其它 code（含缺字段、类型不符）一律抛 SupportError。
 */

import { SupportError, kindForCode } from './errors';

export interface SupportEnvelope<T> {
  code: number;
  data?: T;
  err?: string;
  msg?: string;
  requestId?: string;
}

function asRecord(value: unknown): null | Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** 解析信封；成功返回 data，失败抛结构化 SupportError。 */
export function parseEnvelope<T>(payload: unknown): T {
  const envelope = asRecord(payload);
  if (!envelope) {
    throw new SupportError({
      code: 0,
      kind: 'protocol',
      message: '响应不是合法的 JSON 对象',
    });
  }
  const rawCode = envelope.code;
  if (typeof rawCode !== 'number' || !Number.isInteger(rawCode)) {
    throw new SupportError({
      code: 0,
      kind: 'protocol',
      message: '响应缺少合法的 code 字段',
    });
  }
  if (rawCode !== 0) {
    const msg =
      typeof envelope.msg === 'string' && envelope.msg !== ''
        ? envelope.msg
        : typeof envelope.err === 'string' && envelope.err !== ''
          ? envelope.err
          : `请求失败（code=${rawCode}）`;
    throw new SupportError({
      code: rawCode,
      kind: kindForCode(rawCode),
      message: msg,
      ...(typeof envelope.requestId === 'string'
        ? { requestId: envelope.requestId }
        : {}),
    });
  }
  return envelope.data as T;
}

/** code 0 但没有 data 的场景（例如 logout）返回 undefined 而不是抛错。 */
export function parseEnvelopeVoid(payload: unknown): void {
  parseEnvelope<unknown>(payload);
}